/**
 * 아이콘은 Font Awesome 하나로 통일한다 (lucide-react 제거).
 *
 * lucide 와 같은 이름·props(className, style, ...)를 내보내서 호출부는 import
 * 경로만 바꾸면 된다. FA 무료판에 1:1 대응이 없는 것은 가장 비슷한 걸로
 * 대신한다 — Heading1~3 은 숫자(fa1~3), ShieldX 는 faBan,
 * ArrowLeft/RightToLine 은 일반 화살표.
 *
 * **FA 기본 CSS 는 styles.css 에서 `layer(base)` 로 가져온다** (여기서
 * autoAddCss 를 끈다). unlayered 로 두면 `.svg-inline--fa { height: 1em }` 이
 * Tailwind `h-*` 유틸리티(@layer utilities)를 항상 이겨 크기 지정이 죽는다.
 */
import { config, type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faBell as faBellRegular, faEnvelope as faEnvelopeRegular } from '@fortawesome/free-regular-svg-icons';
import {
  FontAwesomeIcon,
  type FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import {
  fa1,
  fa2,
  fa3,
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faArrowUpRightFromSquare,
  faArrowsDownToLine,
  faArrowsUpToLine,
  faBan,
  faCompass,
  faBars,
  faBell,
  faBold,
  faBookOpen,
  faCheck,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faClock,
  faCloud,
  faCode,
  faCoins,
  faComment,
  faEllipsis,
  faEnvelope,
  faFlag,
  faBookmark,
  faHeart,
  faHighlighter,
  faHouse,
  faImage,
  faInbox,
  faItalic,
  faLink,
  faListOl,
  faListUl,
  faLocationDot,
  faMinus,
  faNewspaper,
  faPaperPlane,
  faPaperclip,
  faPen,
  faPlus,
  faQuoteLeft,
  faRightFromBracket,
  faRightToBracket,
  faShareNodes,
  faShieldHalved,
  faStar,
  faStrikethrough,
  faTable,
  faTableColumns,
  faThumbsDown,
  faThumbsUp,
  faTrashCan,
  faTriangleExclamation,
  faUnderline,
  faUser,
  faUsers,
  faWandMagicSparkles,
  faXmark,
  faCircleXmark,
  faSquarePollVertical,
} from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faSquareYoutube, faXTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';

config.autoAddCss = false;

type IconProps = Omit<FontAwesomeIconProps, 'icon'>;

function make(icon: IconDefinition, name: string) {
  function Icon(props: IconProps) {
    return <FontAwesomeIcon icon={icon} {...props} />;
  }
  Icon.displayName = name;
  return Icon;
}

export const ArrowDownToLine = make(faArrowsDownToLine, 'ArrowDownToLine');
export const ArrowLeftToLine = make(faArrowLeft, 'ArrowLeftToLine');
export const ArrowRightToLine = make(faArrowRight, 'ArrowRightToLine');
export const ArrowUpToLine = make(faArrowsUpToLine, 'ArrowUpToLine');
export const Bell = make(faBell, 'Bell');
export const Bold = make(faBold, 'Bold');
export const BookOpen = make(faBookOpen, 'BookOpen');
export const Check = make(faCheck, 'Check');
export const CircleX = make(faCircleXmark, 'CircleX');
export const CheckIcon = Check;
export const ChevronDownIcon = make(faChevronDown, 'ChevronDownIcon');
export const ChevronLeft = make(faChevronLeft, 'ChevronLeft');
export const ChevronRight = make(faChevronRight, 'ChevronRight');
export const ChevronUpIcon = make(faChevronUp, 'ChevronUpIcon');
export const Clock = make(faClock, 'Clock');
export const Cloud = make(faCloud, 'Cloud');
export const Code = make(faCode, 'Code');
export const Coins = make(faCoins, 'Coins');
export const Columns3 = make(faTableColumns, 'Columns3');
export const ExternalLink = make(faArrowUpRightFromSquare, 'ExternalLink');
export const Heading1 = make(fa1, 'Heading1');
export const Heading2 = make(fa2, 'Heading2');
export const Heading3 = make(fa3, 'Heading3');
export const Heart = make(faHeart, 'Heart');
/** 알림 없을 때의 종 — 윤곽선. 있으면 solid `Bell` + 숫자. */
export const BellOutline = make(faBellRegular, 'BellOutline');
export const Bookmark = make(faBookmark, 'Bookmark');
export const Highlighter = make(faHighlighter, 'Highlighter');
export const Home = make(faHouse, 'Home');
export const Image = make(faImage, 'Image');
export const Inbox = make(faInbox, 'Inbox');
export const Instagram = make(faInstagram, 'Instagram');
export const Italic = make(faItalic, 'Italic');
export const Link = make(faLink, 'Link');
export const Link2 = Link;
export const List = make(faListUl, 'List');
export const ListOrdered = make(faListOl, 'ListOrdered');
export const LogIn = make(faRightToBracket, 'LogIn');
export const LogOut = make(faRightFromBracket, 'LogOut');
export const Compass = make(faCompass, 'Compass');
export const Flag = make(faFlag, 'Flag');
export const Mail = make(faEnvelope, 'Mail');
export const MailOutline = make(faEnvelopeRegular, 'MailOutline');
export const MapPin = make(faLocationDot, 'MapPin');
export const MessageCircle = make(faComment, 'MessageCircle');
export const MessageSquareWarning = make(faTriangleExclamation, 'MessageSquareWarning');
export const Minus = make(faMinus, 'Minus');
export const MoreHorizontal = make(faEllipsis, 'MoreHorizontal');
export const Newspaper = make(faNewspaper, 'Newspaper');
export const Paperclip = make(faPaperclip, 'Paperclip');
export const Pencil = make(faPen, 'Pencil');
export const Plus = make(faPlus, 'Plus');
export const Quote = make(faQuoteLeft, 'Quote');
export const Rows3 = make(faBars, 'Rows3');
export const Poll = make(faSquarePollVertical, 'Poll');
export const Send = make(faPaperPlane, 'Send');
export const Share2 = make(faShareNodes, 'Share2');
export const ShieldCheck = make(faShieldHalved, 'ShieldCheck');
export const ShieldX = make(faBan, 'ShieldX');
export const Sparkles = make(faWandMagicSparkles, 'Sparkles');
export const Star = make(faStar, 'Star');
export const Strikethrough = make(faStrikethrough, 'Strikethrough');
export const Table = make(faTable, 'Table');
export const ThumbsDown = make(faThumbsDown, 'ThumbsDown');
export const ThumbsUp = make(faThumbsUp, 'ThumbsUp');
export const Trash2 = make(faTrashCan, 'Trash2');
export const Underline = make(faUnderline, 'Underline');
export const User = make(faUser, 'User');
export const Users = make(faUsers, 'Users');
export const X = make(faXmark, 'X');
export const Youtube = make(faYoutube, 'Youtube');
export const YoutubeSquare = make(faSquareYoutube, 'YoutubeSquare');
export const XTwitter = make(faXTwitter, 'XTwitter');
